import { formatMeetingTime } from '../../utils/meetingSchedule';
import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    TableSortLabel,
    Chip,
    IconButton,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Grid,
    Card,
    CardContent,
    Alert,
    CircularProgress,
    Tooltip,
    Menu,
    ListItemIcon,
    ListItemText
} from '@mui/material';
import {
    FilterList,
    MoreVert,
    Edit,
    Cancel,
    CheckCircle,
    Event,
    Person,
    Phone,
    LocationOn,
    Schedule,
    Visibility,
    Clear,
    Notifications,
    Delete,
    Autorenew,
    Download
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { es } from 'date-fns/locale';
import { format } from 'date-fns';

import {
    schedulingService,
    ScheduledMeeting,
    MeetingStatus,
    MeetingFilter,
    SchedulingConfig,
    MeetingSortField,
    SortDirection
} from '../../services/schedulingService';
import MeetingNotifications from './MeetingNotifications';
import { useAuth } from '../../contexts/AuthContext';

interface MeetingListProps { }

const MeetingList: React.FC<MeetingListProps> = () => {
    const { user } = useAuth();
    const [meetings, setMeetings] = useState<ScheduledMeeting[]>([]);
    const [configs, setConfigs] = useState<SchedulingConfig[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [busyAction, setBusyAction] = useState(false);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);
    const [totalMeetings, setTotalMeetings] = useState(0);

    // Filters
    const [filters, setFilters] = useState<MeetingFilter>({});
    const [filterDialogOpen, setFilterDialogOpen] = useState(false);
    const [tempFilters, setTempFilters] = useState<MeetingFilter>({});
    const [sortBy, setSortBy] = useState<MeetingSortField | undefined>(undefined);
    const [sortDirection, setSortDirection] = useState<SortDirection | undefined>(undefined);

    // Meeting actions
    const [selectedMeeting, setSelectedMeeting] = useState<ScheduledMeeting | null>(null);
    const [actionMenuAnchor, setActionMenuAnchor] = useState<null | HTMLElement>(null);
    const [detailDialogOpen, setDetailDialogOpen] = useState(false);
    const [statusUpdateDialogOpen, setStatusUpdateDialogOpen] = useState(false);
    const [newStatus, setNewStatus] = useState<MeetingStatus>(MeetingStatus.SCHEDULED);
    const [statusNotes, setStatusNotes] = useState('');
    const [cancellationReason, setCancellationReason] = useState('');
    
    // Notifications
    const [notificationsDialogOpen, setNotificationsDialogOpen] = useState(false);
    const [notificationMeeting, setNotificationMeeting] = useState<ScheduledMeeting | null>(null);
    const [exportDialogOpen, setExportDialogOpen] = useState(false);
    const [exportLoading, setExportLoading] = useState(false);
    const [exportError, setExportError] = useState<string | null>(null);

    const roleName = (user?.role?.name || '').trim().toLowerCase();
    const isAdmin = roleName === 'admin' || roleName === 'super admin';

    useEffect(() => {
        loadMeetings();
    }, [page, rowsPerPage, filters, sortBy, sortDirection]);

    const toLocalDate = (yyyyMmDd: string) => {
        const raw = (yyyyMmDd || '').trim();
        const parts = raw.split('-');
        if (parts.length !== 3) return new Date(raw);
        const year = Number(parts[0]);
        const month = Number(parts[1]);
        const day = Number(parts[2]);
        if (!year || !month || !day) return new Date(raw);
        return new Date(year, month - 1, day);
    };

    const loadMeetings = async () => {
        try {
            setLoading(true);
            setError(null);
            const requestFilters: MeetingFilter = {
                ...filters,
                sort_by: sortBy,
                sort_direction: sortDirection,
            };

            const data = await schedulingService.getScheduledMeetings(requestFilters, page * rowsPerPage, rowsPerPage);
            setMeetings(data);

            // Get total count for pagination
            const totalData = await schedulingService.getScheduledMeetings(requestFilters, 0, 1000);
            setTotalMeetings(totalData.length);

            // Load configurations for filtering
            const configsData = await schedulingService.getSchedulingConfigs();
            setConfigs(configsData);
        } catch (err) {
            console.error('Error loading meetings:', err);
            setError('Error al cargar las citas');
        } finally {
            setLoading(false);
        }
    };

    const extractErrorMessage = (err: any, fallbackMessage: string): string => {
        if (err?.code === 'ECONNABORTED' || `${err?.message || ''}`.toLowerCase().includes('timeout')) {
            return 'La operación tardó demasiado. Intenta nuevamente en unos minutos.';
        }
        const detail = err?.response?.data?.detail;
        if (typeof detail === 'string') {
            return detail;
        }
        if (detail && typeof detail === 'object') {
            return detail.user_message || detail.message || fallbackMessage;
        }
        return err?.message || fallbackMessage;
    };

    const handleChangePage = (event: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleOpenFilterDialog = () => {
        setTempFilters({ ...filters });
        setFilterDialogOpen(true);
    };

    const handleSort = (field: MeetingSortField) => {
        const nextDirection: SortDirection =
            sortBy === field && sortDirection === 'asc' ? 'desc' : 'asc';
        setSortBy(field);
        setSortDirection(nextDirection);
        setPage(0);
    };

    const handleApplyFilters = () => {
        setFilters({ ...tempFilters });
        setPage(0);
        setFilterDialogOpen(false);
    };

    const handleClearFilters = () => {
        setFilters({});
        setTempFilters({});
        setPage(0);
        setFilterDialogOpen(false);
    };

    const downloadBlob = (blob: Blob, filename: string) => {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
    };

    const handleExportMeetings = async () => {
        try {
            setExportLoading(true);
            setExportError(null);
            const blob = await schedulingService.exportMeetings({
                format: 'excel',
                filters: {
                    ...filters,
                    sort_by: sortBy,
                    sort_direction: sortDirection,
                },
            });
            const timestamp = new Date().toISOString().slice(0, 19).replace(/[:-]/g, '');
            downloadBlob(blob, `appointments_export_${timestamp}.xlsx`);
            setExportDialogOpen(false);
        } catch (err) {
            console.error('Error exporting meetings:', err);
            setExportError('Error al exportar las citas. Por favor, intenta nuevamente.');
        } finally {
            setExportLoading(false);
        }
    };

    const handleActionMenuOpen = (event: React.MouseEvent<HTMLElement>, meeting: ScheduledMeeting) => {
        setActionMenuAnchor(event.currentTarget);
        setSelectedMeeting(meeting);
    };

    const handleActionMenuClose = () => {
        setActionMenuAnchor(null);
        setSelectedMeeting(null);
    };

    const handleViewDetails = () => {
        setDetailDialogOpen(true);
        handleActionMenuClose();
    };

    const handleStatusUpdate = () => {
        if (selectedMeeting) {
            setNewStatus(selectedMeeting.status as MeetingStatus);
            setStatusNotes('');
            setCancellationReason('');
            setStatusUpdateDialogOpen(true);
        }
        handleActionMenuClose();
    };

    const handleViewNotifications = () => {
        if (selectedMeeting) {
            setNotificationMeeting(selectedMeeting);
            setNotificationsDialogOpen(true);
        }
        handleActionMenuClose();
    };

    const handleDeleteMeeting = async () => {
        if (!selectedMeeting) return;

        const meetingToDelete = selectedMeeting;
        handleActionMenuClose();

        const customerLabel = getMeetingDisplayName(meetingToDelete) || meetingToDelete.customer_phone || `#${meetingToDelete.id}`;
        const confirmed = window.confirm(`¿Eliminar permanentemente la cita de ${customerLabel}?`);
        if (!confirmed) return;

        try {
            setBusyAction(true);
            await schedulingService.deleteMeeting(meetingToDelete.id);
            await loadMeetings();
            setError(null);
        } catch (err: any) {
            console.error('Error deleting meeting:', err);
            setError(extractErrorMessage(err, 'Error al eliminar la cita'));
        } finally {
            setBusyAction(false);
        }
    };

    const handleRecalculateTimeslots = async () => {
        const confirmed = window.confirm('¿Recalcular todos los timeslots futuros de configuración?');
        if (!confirmed) return;

        try {
            setBusyAction(true);
            const result = await schedulingService.recalculateTimeslots();
            await loadMeetings();
            setError(null);
            window.alert(result?.message || 'Timeslots recalculados correctamente');
        } catch (err: any) {
            console.error('Error recalculating timeslots:', err);
            setError(extractErrorMessage(err, 'Error al recalcular los timeslots'));
        } finally {
            setBusyAction(false);
        }
    };

    const handleSaveStatusUpdate = async () => {
        if (!selectedMeeting) return;

        try {
            await schedulingService.updateMeetingStatus(
                selectedMeeting.id,
                newStatus,
                statusNotes || undefined,
                cancellationReason || undefined
            );

            // Reload meetings
            await loadMeetings();

            // Update selected meeting
            const updatedMeeting = await schedulingService.getScheduledMeeting(selectedMeeting.id);
            setSelectedMeeting(updatedMeeting);

            setStatusUpdateDialogOpen(false);
        } catch (err) {
            console.error('Error updating meeting status:', err);
            setError('Error al actualizar el estado de la cita');
        }
    };

    const getStatusColor = (status: MeetingStatus): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' => {
        switch (status) {
            case MeetingStatus.SCHEDULED:
                return 'info';
            case MeetingStatus.CONFIRMED:
                return 'primary';
            case MeetingStatus.COMPLETED:
                return 'success';
            case MeetingStatus.CANCELLED:
                return 'error';
            case MeetingStatus.NO_SHOW:
                return 'warning';
            default:
                return 'default';
        }
    };

    const getStatusLabel = (status: MeetingStatus): string => {
        switch (status) {
            case MeetingStatus.SCHEDULED:
                return 'Programada';
            case MeetingStatus.CONFIRMED:
                return 'Confirmada';
            case MeetingStatus.COMPLETED:
                return 'Completada';
            case MeetingStatus.CANCELLED:
                return 'Cancelada';
            case MeetingStatus.NO_SHOW:
                return 'No Asistió';
            default:
                return status;
        }
    };

    const getMeetingDisplayName = (meeting: ScheduledMeeting): string => {
        return (
            meeting.customer_display_name?.trim() ||
            meeting.customer_name?.trim() ||
            meeting.customer_phone ||
            'Cliente sin nombre'
        );
    };

    const getMeetingHubspotEmail = (meeting: ScheduledMeeting): string => {
        return meeting.customer_email?.trim() || 'Sin email';
    };

    const getMeetingHubspotId = (meeting: ScheduledMeeting): string => {
        return meeting.hubspot_contact_id?.trim() || 'Sin HubSpot ID';
    };

    const renderSortLabel = (label: string, field: MeetingSortField) => (
        <TableSortLabel
            active={sortBy === field}
            direction={sortBy === field ? sortDirection || 'asc' : 'asc'}
            onClick={() => handleSort(field)}
        >
            {label}
        </TableSortLabel>
    );

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress />
            </Box>
        );
    }

    return (
        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={es}>
            <Box>
                {/* Header with filters */}
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                    <Typography variant="h6">
                        Lista de Citas ({totalMeetings} total)
                    </Typography>
                    <Box display="flex" gap={1}>
                        {isAdmin && (
                            <Button
                                variant="outlined"
                                color="secondary"
                                startIcon={<Autorenew />}
                                onClick={handleRecalculateTimeslots}
                                disabled={busyAction || loading}
                            >
                                Recalcular Timeslots
                            </Button>
                        )}
                        <Button
                            variant="outlined"
                            startIcon={<Download />}
                            onClick={() => {
                                setExportError(null);
                                setExportDialogOpen(true);
                            }}
                            disabled={busyAction || loading}
                        >
                            Exportar
                        </Button>
                        <Button
                            variant="outlined"
                            startIcon={<FilterList />}
                            onClick={handleOpenFilterDialog}
                            disabled={busyAction}
                        >
                            Filtros
                        </Button>
                    </Box>
                </Box>

                {error && (
                    <Alert severity="error" sx={{ mb: 3 }}>
                        {error}
                    </Alert>
                )}

                {/* Active filters display */}
                {Object.keys(filters).length > 0 && (
                    <Card sx={{ mb: 3, p: 2 }}>
                        <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                            <Typography variant="body2" color="text.secondary">
                                Filtros activos:
                            </Typography>
                            {filters.statuses && filters.statuses.length > 0 && (
                                <Chip
                                    label={`Estado: ${getStatusLabel(filters.statuses[0])}`}
                                    size="small"
                                    onDelete={() => setFilters({ ...filters, statuses: undefined })}
                                />
                            )}
                            {filters.project_codes && filters.project_codes.length > 0 && (
                                <Chip
                                    label={`Proyecto: ${filters.project_codes[0]}`}
                                    size="small"
                                    onDelete={() => setFilters({ ...filters, project_codes: undefined })}
                                />
                            )}
                            {filters.start_date && (
                                <Chip
                                    label={`Desde: ${filters.start_date}`}
                                    size="small"
                                    onDelete={() => setFilters({ ...filters, start_date: undefined })}
                                />
                            )}
                            {filters.end_date && (
                                <Chip
                                    label={`Hasta: ${filters.end_date}`}
                                    size="small"
                                    onDelete={() => setFilters({ ...filters, end_date: undefined })}
                                />
                            )}
                            <Button
                                size="small"
                                startIcon={<Clear />}
                                onClick={() => setFilters({})}
                            >
                                Limpiar todos
                            </Button>
                        </Box>
                    </Card>
                )}

                {/* Meetings table */}
                <TableContainer component={Paper}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>{renderSortLabel('Fecha y Hora', 'meeting_datetime')}</TableCell>
                                <TableCell>{renderSortLabel('Cliente', 'customer')}</TableCell>
                                <TableCell>{renderSortLabel('HubSpot', 'hubspot')}</TableCell>
                                <TableCell>{renderSortLabel('Proyecto', 'project')}</TableCell>
                                <TableCell>{renderSortLabel('Showroom', 'showroom_location')}</TableCell>
                                <TableCell>{renderSortLabel('Estado', 'status')}</TableCell>
                                <TableCell>{renderSortLabel('Contacto Ventas', 'sales_contact_name')}</TableCell>
                                <TableCell align="right">Acciones</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {meetings.map((meeting) => (
                                <TableRow key={meeting.id} hover>
                                    <TableCell>
                                        <Box>
                                            <Typography variant="body2" fontWeight="bold">
                                                {format(toLocalDate(meeting.meeting_date), 'dd MMM yyyy', { locale: es })}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {formatMeetingTime(meeting)}
                                            </Typography>
                                        </Box>
                                    </TableCell>
                                    <TableCell>
                                        <Box>
                                            <Typography variant="body2" fontWeight="bold">
                                                {getMeetingDisplayName(meeting)}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {meeting.customer_phone}
                                            </Typography>
                                        </Box>
                                    </TableCell>
                                    <TableCell>
                                        <Box>
                                            <Typography variant="body2" fontWeight="bold">
                                                {getMeetingHubspotEmail(meeting)}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {getMeetingHubspotId(meeting)}
                                            </Typography>
                                        </Box>
                                    </TableCell>
                                    <TableCell>
                                        <Box>
                                            <Typography variant="body2">
                                                {meeting.project_name}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {meeting.project_code}
                                            </Typography>
                                        </Box>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2">
                                            {meeting.showroom_location}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            label={getStatusLabel(meeting.status as MeetingStatus)}
                                            color={getStatusColor(meeting.status as MeetingStatus)}
                                            size="small"
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2">
                                            {meeting.sales_contact_name}
                                        </Typography>
                                    </TableCell>
                                    <TableCell align="right">
                                        <IconButton
                                            size="small"
                                            onClick={(e) => handleActionMenuOpen(e, meeting)}
                                        >
                                            <MoreVert />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>

                {/* Pagination */}
                <TablePagination
                    rowsPerPageOptions={[10, 25, 50, 100]}
                    component="div"
                    count={totalMeetings}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    labelRowsPerPage="Filas por página:"
                    labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
                />

                {/* Action Menu */}
                <Menu
                    anchorEl={actionMenuAnchor}
                    open={Boolean(actionMenuAnchor)}
                    onClose={handleActionMenuClose}
                >
                    <MenuItem onClick={handleViewDetails}>
                        <ListItemIcon>
                            <Visibility fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>Ver Detalles</ListItemText>
                    </MenuItem>
                    <MenuItem onClick={handleStatusUpdate}>
                        <ListItemIcon>
                            <Edit fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>Actualizar Estado</ListItemText>
                    </MenuItem>
                    <MenuItem onClick={handleViewNotifications}>
                        <ListItemIcon>
                            <Notifications fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>Ver Notificaciones</ListItemText>
                    </MenuItem>
                    {isAdmin && (
                        <MenuItem onClick={handleDeleteMeeting} disabled={busyAction}>
                            <ListItemIcon>
                                <Delete fontSize="small" />
                            </ListItemIcon>
                            <ListItemText>Eliminar Cita</ListItemText>
                        </MenuItem>
                    )}
                </Menu>

                {/* Filter Dialog */}
                <Dialog open={filterDialogOpen} onClose={() => setFilterDialogOpen(false)} maxWidth="md" fullWidth>
                    <DialogTitle>
                        Filtrar Citas
                    </DialogTitle>
                    <DialogContent>
                        <Grid container spacing={3} sx={{ mt: 1 }}>
                            <Grid item xs={12} md={6}>
                                <FormControl fullWidth>
                                    <InputLabel>Estado</InputLabel>
                                    <Select
                                        value={tempFilters.statuses?.[0] || ''}
                                        onChange={(e) => setTempFilters({ ...tempFilters, statuses: e.target.value ? [e.target.value as MeetingStatus] : undefined })}
                                        label="Estado"
                                    >
                                        <MenuItem value="">Todos</MenuItem>
                                        <MenuItem value={MeetingStatus.SCHEDULED}>Programada</MenuItem>
                                        <MenuItem value={MeetingStatus.CONFIRMED}>Confirmada</MenuItem>
                                        <MenuItem value={MeetingStatus.COMPLETED}>Completada</MenuItem>
                                        <MenuItem value={MeetingStatus.CANCELLED}>Cancelada</MenuItem>
                                        <MenuItem value={MeetingStatus.NO_SHOW}>No Asistió</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <FormControl fullWidth>
                                    <InputLabel>Proyecto</InputLabel>
                                    <Select
                                        value={tempFilters.project_codes?.[0] || ''}
                                        onChange={(e) => setTempFilters({ ...tempFilters, project_codes: e.target.value ? [e.target.value] : undefined })}
                                        label="Proyecto"
                                    >
                                        <MenuItem value="">Todos</MenuItem>
                                        {configs.map((config) => (
                                            <MenuItem key={config.project_code} value={config.project_code}>
                                                {config.project_name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <DatePicker
                                    label="Fecha Desde"
                                    value={tempFilters.start_date ? toLocalDate(tempFilters.start_date) : null}
                                    onChange={(newValue) => {
                                        setTempFilters({
                                            ...tempFilters,
                                            start_date: newValue ? format(newValue as Date, 'yyyy-MM-dd') : undefined
                                        });
                                    }}
                                    renderInput={(params) => <TextField {...params} fullWidth />}
                                />
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <DatePicker
                                    label="Fecha Hasta"
                                    value={tempFilters.end_date ? toLocalDate(tempFilters.end_date) : null}
                                    onChange={(newValue) => {
                                        setTempFilters({
                                            ...tempFilters,
                                            end_date: newValue ? format(newValue as Date, 'yyyy-MM-dd') : undefined
                                        });
                                    }}
                                    renderInput={(params) => <TextField {...params} fullWidth />}
                                />
                            </Grid>


                        </Grid>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setFilterDialogOpen(false)}>
                            Cancelar
                        </Button>
                        <Button onClick={handleClearFilters}>
                            Limpiar
                        </Button>
                        <Button onClick={handleApplyFilters} variant="contained">
                            Aplicar Filtros
                        </Button>
                    </DialogActions>
                </Dialog>

                <Dialog
                    open={exportDialogOpen}
                    onClose={() => !exportLoading && setExportDialogOpen(false)}
                    maxWidth="sm"
                    fullWidth
                >
                    <DialogTitle>Exportar Citas</DialogTitle>
                    <DialogContent>
                        <Box display="flex" flexDirection="column" gap={2} sx={{ mt: 1 }}>
                            {exportError && (
                                <Alert severity="error" onClose={() => setExportError(null)}>
                                    {exportError}
                                </Alert>
                            )}
                            <Typography variant="body2">
                                Se descargará un archivo Excel con todas las citas que coincidan con los filtros actuales.
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                El orden exportado seguirá la clasificación activa de la tabla.
                            </Typography>
                        </Box>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setExportDialogOpen(false)} disabled={exportLoading}>
                            Cancelar
                        </Button>
                        <Button
                            onClick={handleExportMeetings}
                            variant="contained"
                            disabled={exportLoading}
                            startIcon={exportLoading ? <CircularProgress size={18} /> : <Download />}
                        >
                            {exportLoading ? 'Exportando...' : 'Exportar Excel'}
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Meeting Detail Dialog */}
                <Dialog open={detailDialogOpen} onClose={() => setDetailDialogOpen(false)} maxWidth="md" fullWidth>
                    <DialogTitle>
                        Detalles de la Cita
                    </DialogTitle>
                    <DialogContent>
                        {selectedMeeting && (
                            <Grid container spacing={3} sx={{ mt: 1 }}>
                                <Grid item xs={12} md={6}>
                                    <Typography variant="subtitle2" gutterBottom>
                                        Información del Cliente
                                    </Typography>
                                    <Typography variant="body1" gutterBottom>
                                        <strong>Nombre:</strong> {getMeetingDisplayName(selectedMeeting)}
                                    </Typography>
                                    <Typography variant="body1" gutterBottom>
                                        <strong>Teléfono:</strong> {selectedMeeting.customer_phone}
                                    </Typography>
                                    {selectedMeeting.customer_email && (
                                        <Typography variant="body1" gutterBottom>
                                            <strong>Email:</strong> {selectedMeeting.customer_email}
                                        </Typography>
                                    )}
                                    {selectedMeeting.hubspot_contact_id && (
                                        <Typography variant="body1" gutterBottom>
                                            <strong>HubSpot ID:</strong> {selectedMeeting.hubspot_contact_id}
                                        </Typography>
                                    )}
                                </Grid>

                                <Grid item xs={12} md={6}>
                                    <Typography variant="subtitle2" gutterBottom>
                                        Información de la Cita
                                    </Typography>
                                    <Typography variant="body1" gutterBottom>
                                        <strong>Fecha:</strong> {format(toLocalDate(selectedMeeting.meeting_date), 'dd MMMM yyyy', { locale: es })}
                                    </Typography>
                                    <Typography variant="body1" gutterBottom>
                                        <strong>Hora:</strong> {formatMeetingTime(selectedMeeting)}
                                    </Typography>
                                    <Typography variant="body1" gutterBottom>
                                        <strong>Estado:</strong>
                                        <Chip
                                            label={getStatusLabel(selectedMeeting.status as MeetingStatus)}
                                            color={getStatusColor(selectedMeeting.status as MeetingStatus)}
                                            size="small"
                                            sx={{ ml: 1 }}
                                        />
                                    </Typography>
                                </Grid>

                                <Grid item xs={12} md={6}>
                                    <Typography variant="subtitle2" gutterBottom>
                                        Información del Proyecto
                                    </Typography>
                                    <Typography variant="body1" gutterBottom>
                                        <strong>Proyecto:</strong> {selectedMeeting.project_name}
                                    </Typography>
                                    <Typography variant="body1" gutterBottom>
                                        <strong>Código:</strong> {selectedMeeting.project_code}
                                    </Typography>
                                    <Typography variant="body1" gutterBottom>
                                        <strong>Showroom:</strong> {selectedMeeting.showroom_location}
                                    </Typography>
                                </Grid>

                                <Grid item xs={12} md={6}>
                                    <Typography variant="subtitle2" gutterBottom>
                                        Contacto de Ventas
                                    </Typography>
                                    <Typography variant="body1" gutterBottom>
                                        <strong>Nombre:</strong> {selectedMeeting.sales_contact_name}
                                    </Typography>
                                    <Typography variant="body1" gutterBottom>
                                        <strong>Teléfono:</strong> {selectedMeeting.sales_contact_phone}
                                    </Typography>
                                    {selectedMeeting.sales_contact_email && (
                                        <Typography variant="body1" gutterBottom>
                                            <strong>Email:</strong> {selectedMeeting.sales_contact_email}
                                        </Typography>
                                    )}
                                </Grid>

                                {selectedMeeting.customer_notes && (
                                    <Grid item xs={12}>
                                        <Typography variant="subtitle2" gutterBottom>
                                            Notas del Cliente
                                        </Typography>
                                        <Typography variant="body1">
                                            {selectedMeeting.customer_notes}
                                        </Typography>
                                    </Grid>
                                )}

                                {selectedMeeting.internal_notes && (
                                    <Grid item xs={12}>
                                        <Typography variant="subtitle2" gutterBottom>
                                            Notas Internas
                                        </Typography>
                                        <Typography variant="body1">
                                            {selectedMeeting.internal_notes}
                                        </Typography>
                                    </Grid>
                                )}
                            </Grid>
                        )}
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setDetailDialogOpen(false)}>
                            Cerrar
                        </Button>
                        <Button
                            variant="contained"
                            startIcon={<Edit />}
                            onClick={handleStatusUpdate}
                        >
                            Actualizar Estado
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Status Update Dialog */}
                <Dialog open={statusUpdateDialogOpen} onClose={() => setStatusUpdateDialogOpen(false)} maxWidth="sm" fullWidth>
                    <DialogTitle>
                        Actualizar Estado de la Cita
                    </DialogTitle>
                    <DialogContent>
                        <Grid container spacing={3} sx={{ mt: 1 }}>
                            <Grid item xs={12}>
                                <FormControl fullWidth>
                                    <InputLabel>Estado</InputLabel>
                                    <Select
                                        value={newStatus}
                                        onChange={(e) => setNewStatus(e.target.value as MeetingStatus)}
                                        label="Estado"
                                    >
                                        <MenuItem value={MeetingStatus.SCHEDULED}>Programada</MenuItem>
                                        <MenuItem value={MeetingStatus.CONFIRMED}>Confirmada</MenuItem>
                                        <MenuItem value={MeetingStatus.COMPLETED}>Completada</MenuItem>
                                        <MenuItem value={MeetingStatus.CANCELLED}>Cancelada</MenuItem>
                                        <MenuItem value={MeetingStatus.NO_SHOW}>No Asistió</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>

                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    multiline
                                    rows={3}
                                    label="Notas"
                                    value={statusNotes}
                                    onChange={(e) => setStatusNotes(e.target.value)}
                                    placeholder="Agregar notas sobre el cambio de estado..."
                                />
                            </Grid>

                            {newStatus === MeetingStatus.CANCELLED && (
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label="Razón de Cancelación"
                                        value={cancellationReason}
                                        onChange={(e) => setCancellationReason(e.target.value)}
                                        placeholder="Especificar la razón de la cancelación..."
                                    />
                                </Grid>
                            )}
                        </Grid>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setStatusUpdateDialogOpen(false)}>
                            Cancelar
                        </Button>
                        <Button
                            variant="contained"
                            onClick={handleSaveStatusUpdate}
                        >
                            Actualizar
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Meeting Notifications Dialog */}
                {notificationMeeting && (
                    <MeetingNotifications
                        meeting={notificationMeeting}
                        open={notificationsDialogOpen}
                        onClose={() => {
                            setNotificationsDialogOpen(false);
                            setNotificationMeeting(null);
                        }}
                    />
                )}
            </Box>
        </LocalizationProvider>
    );
};

export default MeetingList;
